import { reconcileAvatarSelection } from "./avatar-deletion";
import { avatarLookupId, sameAvatarIdentity, toAvatarSelection } from "./avatar-selection-identity";

const result = (status, persisted = false) => ({ status, persisted });

export function createAvatarSelectionController({
  store,
  dashboardChannel,
  fetchAvatarImpl,
  resolveFallbackImpl,
  persistenceApiUrl,
  onChanged = () => {},
  onApplied = () => {},
  onError = () => {}
}) {
  let disposed = false;
  let epoch = 0;
  let generation = 0;
  let account;
  let savedUrl;
  let workspace;
  let baseline;
  let applied;
  let persistedSelection;
  let ready = Promise.resolve();
  let pending;
  let writeTail = Promise.resolve();

  const accountCurrent = token => !disposed && epoch === token && (store.state.authUser?.id ?? null) === account;
  const reportError = error => {
    onError(error);
    return result("failed");
  };
  const notifyApplied = (selection, forceReload = false) => {
    const changed = applied === undefined || !sameAvatarIdentity(applied, selection);
    applied = selection;
    if (changed || forceReload) onApplied({ forceReload });
  };

  function synchronize({ accountId = null, avatarUrl = null, workspaceId = null }) {
    if (disposed) return Promise.resolve(result("stale"));
    workspace = workspaceId;
    if (account === accountId && savedUrl === avatarUrl) return ready;
    account = accountId;
    savedUrl = avatarUrl;
    const token = ++epoch;
    ++generation;
    pending = null;
    baseline = undefined;
    persistedSelection = toAvatarSelection(avatarUrl, null);
    const id = avatarLookupId(avatarUrl || store.state.profile?.avatarId, persistenceApiUrl);
    ready = (async () => {
      const entry = id == null ? null : await fetchAvatarImpl(id);
      if (!accountCurrent(token)) return result("stale");
      // Failed account restoration must not replace a usable default or invent a baseline.
      if (
        id != null &&
        (!entry ||
          !toAvatarSelection(id, entry)?.avatarUrl ||
          !sameAvatarIdentity(toAvatarSelection(id, null), toAvatarSelection(null, entry)))
      ) {
        return result("failed");
      }
      const restored = await reconcileAvatarSelection({
        avatarId: id,
        currentAvatarUrl: avatarUrl,
        workspaceId: workspace,
        store,
        dashboardChannel,
        fetchAvatarImpl: async () => entry,
        resolveFallbackImpl: async () => null,
        persist: false,
        isCurrent: () => accountCurrent(token)
      });
      if (!accountCurrent(token) || restored.stale) return result("stale");
      if (restored.fallback) return result("failed");
      baseline = restored.selection;
      if (avatarUrl) persistedSelection = restored.selection;
      notifyApplied(restored.selection);
      return result("applied");
    })().catch(error => (accountCurrent(token) ? reportError(error) : result("stale")));
    return ready;
  }

  function select({ avatarId, source = "user", forceReload = false }) {
    if (disposed) return Promise.resolve(result("stale"));
    if (account === undefined) {
      void synchronize({
        accountId: store.state.authUser?.id ?? null,
        avatarUrl: store.state.authUser?.avatar ?? null
      });
    }
    const target = avatarId == null ? null : String(avatarId);
    if (pending && pending.target === target && pending.source === source) {
      pending.forceReload ||= forceReload;
      return pending.promise;
    }
    const token = epoch;
    const operation = ++generation;
    const current = () => accountCurrent(token) && operation === generation;
    const request = { target, source, forceReload };
    pending = request;
    request.promise = (async () => {
      await ready;
      if (!current()) return result("stale");
      const resolved = await reconcileAvatarSelection({
        avatarId: target,
        currentAvatarUrl: savedUrl,
        workspaceId: workspace,
        store,
        dashboardChannel,
        fetchAvatarImpl,
        resolveFallbackImpl,
        persist: false,
        isCurrent: current
      });
      if (!current() || resolved.stale) return result("stale");
      const selection = resolved.selection;
      notifyApplied(selection, request.forceReload);
      const changed = baseline !== undefined && !sameAvatarIdentity(baseline, selection);
      let persisted = false;
      if (account != null) {
        // Compare inside the queue: an older accepted write may have changed the server
        // even when its local result was discarded. The latest operation must write last.
        const write = writeTail.then(async () => {
          if (!current()) return false;
          if (!sameAvatarIdentity(persistedSelection, selection)) {
            await dashboardChannel.updateCurrentUserProfile({ avatar: selection?.avatarUrl ?? null });
            if (accountCurrent(token)) persistedSelection = selection;
          }
          if (!current()) return false;
          // Recognize the React/store echo before publishing the new account value.
          savedUrl = selection?.avatarUrl ?? null;
          if (store.state.authUser?.avatar !== savedUrl) {
            store.update({ authUser: { ...store.state.authUser, avatar: savedUrl } });
          }
          return true;
        });
        writeTail = write.catch(() => {});
        persisted = await write;
      }
      if (!current()) return result("stale");
      if (account != null && !persisted) return result("failed");
      baseline = selection;
      if (resolved.fallback) return result("failed", persisted);
      if (source === "user" && changed) onChanged();
      return result(changed ? "applied" : "unchanged", persisted);
    })()
      .catch(error => (current() ? reportError(error) : result("stale")))
      .finally(() => {
        if (pending === request) pending = null;
      });
    return request.promise;
  }

  return {
    synchronize,
    select,
    dispose() {
      disposed = true;
      ++epoch;
      ++generation;
      pending = null;
    }
  };
}
